import {
  createContext,
  useCallback,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { workspaceStore } from '@/auth/stores';
import { useAuth } from '@/hooks/useAuth';
import type { WorkspaceSummary } from '@/types/api';
import { WorkspaceRole } from '@/types/enums';

interface WorkspaceContextValue {
  workspaces: WorkspaceSummary[];
  active: WorkspaceSummary | null;
  activeId: string | null;
  role: WorkspaceRole | null;
  isOwner: boolean;
  isAdmin: boolean;
  switchTo: (workspaceId: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WorkspaceContext = createContext<WorkspaceContextValue | null>(
  null
);

/**
 * Which workspace the app is showing, and what the user may do in it.
 *
 * Derived from the memberships already on `/auth/me`, so selecting one costs no
 * extra request.
 */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { workspaces } = useAuth();
  const queryClient = useQueryClient();

  const storedId = useSyncExternalStore(
    workspaceStore.subscribe,
    workspaceStore.get
  );

  // The stored id is always validated against current membership rather than
  // trusted. It covers two real cases: another user signed in on this browser
  // and left theirs behind, and this user was removed from the workspace since
  // their last visit. Falling back to the first membership handles both.
  const active =
    workspaces.find((workspace) => workspace.id === storedId) ??
    workspaces[0] ??
    null;

  const switchTo = useCallback(
    (workspaceId: string) => {
      if (workspaceId === active?.id) return;
      workspaceStore.set(workspaceId);
      // Every cached list belongs to the workspace it was fetched under.
      // Clearing rather than invalidating also kills the `placeholderData`
      // sources, which would otherwise render the previous workspace's rows
      // for a frame while the new ones load.
      queryClient.clear();
    },
    [active?.id, queryClient]
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspaces,
      active,
      activeId: active?.id ?? null,
      role: active?.role ?? null,
      isOwner: active?.role === WorkspaceRole.OWNER,
      isAdmin:
        active?.role === WorkspaceRole.OWNER ||
        active?.role === WorkspaceRole.ADMIN,
      switchTo,
    }),
    [workspaces, active, switchTo]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
